import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { logoutUser } from "../store/authSlice";
import {
  MdDashboard,
  MdAttachMoney,
  MdFlag,
  MdPeople,
  MdCampaign,
  MdAccountBalance,
  MdRequestPage,
  MdAnalytics,
  MdEvent,
  MdRefresh,
  MdContentPaste,
  MdReceipt,
  MdEmail,
  MdFilterList,
  MdWebAsset,
  MdCardGiftcard,
  MdExpandMore,
  MdChevronRight,
  MdImportContacts,
  MdTrendingUp,
  MdDescription,
  MdSettings
} from "react-icons/md";
import type { IconType } from "react-icons";

// Constants - Using CSS Variables
const COLORS = {
  primary: "var(--primary)",
  primaryLight: "var(--primary-light)",
  primaryBorder: "var(--primary-border)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-tertiary)",
  textMuted: "var(--text-muted)",
  border: "var(--border-primary)",
  borderLight: "var(--border-light)",
} as const;

const SIZES = {
  sidebarWidth: "280px",
  sidebarWidthCollapsed: "80px",
  logo: "40px",
  icon: "20px",
} as const;

type PageId = "dashboard" | "donations" | "contact-management" | "circles" | "giving-history" | "campaigns" | "funds" | "appeals" | "crowdfunding" | "analytics" | "event-management" | "volunteers" | "refund-request" | "navigation" | "footer" | "transaction-history" | "bulk-emails" | "custom-query" | "pages" | "data-entry-and-import" | "prospects" | "newsletter-subscribers" | "reports" | "eof-statements" | "crm-users" | "receipt-settings";

interface NavigationItem {
  id: PageId | "gift-management";
  label: string;
  icon: IconType;
  children?: NavigationItem[];
}

interface SideNavigationProps {
  currentPage: PageId;
  onPageChange: (page: PageId) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobileOpen: boolean;
  onMobileToggle: () => void;
}

const SideNavigation: React.FC<SideNavigationProps> = ({
  currentPage,
  onPageChange,
  isCollapsed,
  onToggleCollapse,
  isMobileOpen,
  onMobileToggle,
}) => {
  const dispatch = useAppDispatch();
  const { user, navigation } = useAppSelector((state) => state.auth);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [collapsedMenuOpen, setCollapsedMenuOpen] = useState<string | null>(null);
  console.log(navigation);

  // Toggle group expansion
  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  }, []);

  // Toggle collapsed menu
  const toggleCollapsedMenu = useCallback((groupId: string) => {
    setCollapsedMenuOpen(prev => prev === groupId ? null : groupId);
  }, []);

  // Close collapsed menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (collapsedMenuOpen && !target.closest('.collapsed-menu-container')) {
        setCollapsedMenuOpen(null);
      }
    };

    if (collapsedMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [collapsedMenuOpen]);

  // Close collapsed menu when sidebar expands
  useEffect(() => {
    if (!isCollapsed) {
      setCollapsedMenuOpen(null);
    }
  }, [isCollapsed]);

  // On mobile, always show full sidebar (not collapsed)
  const shouldShowCollapsed = isCollapsed && !isMobileOpen;
  // Memoize navigation items to prevent unnecessary re-renders
  const navigationItems: NavigationItem[] = useMemo(
    () => {
      const allNavigationItems: NavigationItem[] = [
        {
          id: "contact-management",
          label: "Contact Management",
          icon: MdPeople,
        },

        {
          id: "gift-management",
          label: "Gift Management",
          icon: MdCardGiftcard,
          children: [
            {
              id: "donations",
              label: "Donations",
              icon: MdAttachMoney,
            },
            {
              id: "campaigns",
              label: "Campaigns",
              icon: MdCampaign,
            },
            {
              id: "funds",
              label: "Funds",
              icon: MdAccountBalance,
            },
            {
              id: "appeals",
              label: "Appeals",
              icon: MdRequestPage,
            },
            {
              id: "crowdfunding",
              label: "Crowdfunding",
              icon: MdPeople,
            },
            {
              id: "data-entry-and-import",
              label: "Data Entry and Import",
              icon: MdImportContacts,
            },
            {
              id: "eof-statements",
              label: "EOFY Statements",
              icon: MdReceipt,
            },
          ],
        },
        {
          id: "prospects",
          label: "Prospects",
          icon: MdTrendingUp,
        },
        {
          id: "dashboard",
          label: "Dashboard",
          icon: MdDashboard,
        },

        {
          id: "circles",
          label: "Circles",
          icon: MdFlag,
        },

        {
          id: "analytics",
          label: "Analytics and reporting",
          icon: MdAnalytics,
        },
        {
          id: "event-management",
          label: "Event Management",
          icon: MdEvent,
        },
        {
          id: "volunteers",
          label: "Volunteers",
          icon: MdPeople,
        },
        {
          id: "refund-request",
          label: "Refund Request",
          icon: MdRefresh,
        },

        {
          id: "transaction-history",
          label: "Transaction History",
          icon: MdReceipt,
        },
        {
          id: "bulk-emails",
          label: "Bulk Emails",
          icon: MdEmail,
        },
        {
          id: "newsletter-subscribers",
          label: "Newsletter Subscribers",
          icon: MdPeople,
        },
        {
          id: "custom-query",
          label: "Custom Query",
          icon: MdFilterList,
        },
        {
          id: "pages",
          label: "Pages",
          icon: MdWebAsset,
        },
        {
          id: "navigation",
          label: "Navigation",
          icon: MdContentPaste,
        },
        {
          id: "footer",
          label: "Footer",
          icon: MdDescription,
        },
        {
          id: "reports",
          label: "Reports",
          icon: MdAnalytics,
        },
        {
          id: "crm-users",
          label: "CRM Users",
          icon: MdPeople,
        },
        {
          id: "receipt-settings",
          label: "Receipt Settings",
          icon: MdSettings,
        },
      ];

      // If navigation array is empty or undefined, show all navigation items
      if (!navigation || navigation.length === 0) {
        return allNavigationItems;
      }

      // Filter navigation items based on the navigation array from auth state
      return allNavigationItems.map(item => {
        // Handle items with children (like Gift Management)
        if (item.children && item.children.length > 0) {
          // Filter children based on navigation permissions
          const filteredChildren = item.children.filter(child =>
            navigation.includes(child.label) ||
            (child.id === "eof-statements" && (navigation.includes("Donations") || navigation.includes("Gift Management")))
          );

          // If Gift Management itself is allowed or any child is allowed, show it
          if (navigation.includes(item.label) || filteredChildren.length > 0) {
            return {
              ...item,
              children: navigation.includes(item.label) ? item.children : filteredChildren
            };
          }
          return null;
        }

        // Regular items without children
        const isAllowed = navigation.includes(item.label) ||
          (item.id === "newsletter-subscribers" && navigation.includes("Bulk Emails")) ||
          (item.id === "reports" && ["global", "donorCare"].includes(user?.role || "")) ||
          (item.id === "crm-users" && user?.role === "global") ||
          (item.id === "receipt-settings" && user?.role === "global");

        return isAllowed ? item : null;
      }).filter((item): item is NavigationItem => item !== null);
    },
    [navigation]
  );

  // Automatically expand parent group of the active child page
  useEffect(() => {
    navigationItems.forEach(item => {
      if (item.children?.some(child => child.id === currentPage)) {
        setExpandedGroups(prev => {
          if (!prev.includes(item.id)) {
            return [...prev, item.id];
          }
          return prev;
        });
      }
    });
  }, [currentPage, navigationItems]);



  // Memoize page change handler
  const handlePageChange = useCallback(
    (pageId: PageId) => {
      onPageChange(pageId);
      // Close mobile menu when navigating
      if (isMobileOpen) {
        onMobileToggle();
      }
    },
    [onPageChange, isMobileOpen, onMobileToggle]
  );

  // Logout handler
  const handleLogout = useCallback(async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      setShowUserMenu(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [dispatch]);

  // Keyboard shortcut handler for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        const searchInput = document.getElementById(
          "search-input"
        ) as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showUserMenu && !target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Check if click is outside both the sidebar and mobile menu container
      if (isMobileOpen && !target.closest('.sidebar-desktop') && !target.closest('.mobile-menu-container')) {
        onMobileToggle();
      }
    };

    if (isMobileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMobileOpen, onMobileToggle]);

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 mobile-overlay"
          onClick={onMobileToggle}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-white border-r transition-all duration-300 z-50 sidebar-desktop
          ${shouldShowCollapsed ? 'w-20' : 'w-70'}
          ${isMobileOpen ? 'sidebar-mobile-open' : ''}
        `}
        style={{
          width: shouldShowCollapsed ? SIZES.sidebarWidthCollapsed : SIZES.sidebarWidth,
          borderColor: "var(--border-primary)",
        }}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex flex-col h-full">
          {/* Header Section */}
          <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--border-primary)" }}>
            {!shouldShowCollapsed && (
              <div className="flex items-center">
                <img
                  src="/assets/logo-circle.png"
                  alt="Penny Appeal GMS Logo"
                  className="w-10 h-10"
                  width={40}
                  height={40}
                />
                <span className="ml-3 text-lg font-semibold" style={{ color: COLORS.textPrimary }}>
                  Penny Appeal
                </span>
              </div>
            )}

            {shouldShowCollapsed && (
              <div className="flex justify-center w-full">
                <img
                  src="/assets/logo-circle.png"
                  alt="Penny Appeal GMS Logo"
                />
              </div>
            )}

            {/* Collapse Toggle Button - Desktop Only */}
            <button
              onClick={onToggleCollapse}
              className="hidden desktop-only items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <svg
                className={`w-4 h-4 transition-transform ${isCollapsed ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                />
              </svg>
            </button>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navigationItems.map((item) => {
              console.log(item);
              const Icon = item.icon;
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = expandedGroups.includes(item.id);
              const isChildActive = hasChildren && item.children?.some(child => child.id === currentPage);

              // Item with children (collapsible group)
              if (hasChildren) {
                return (
                  <div key={item.id} className="collapsed-menu-container relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // If mobile menu is open OR sidebar is expanded, just toggle children
                        if (isMobileOpen || !isCollapsed) {
                          toggleGroup(item.id);
                        } else if (isCollapsed) {
                          // Sidebar is collapsed (desktop), expand it and open children
                          onToggleCollapse();
                          setTimeout(() => {
                            if (!expandedGroups.includes(item.id)) {
                              toggleGroup(item.id);
                            }
                          }, 50);
                        }
                      }}
                      className={`
                        flex items-center w-full p-3 rounded-lg text-sm font-medium transition-colors focus:outline-none
                        ${isChildActive ? 'border' : 'hover:bg-gray-50'}
                        ${shouldShowCollapsed ? 'justify-center' : ''}
                      `}
                      style={{
                        backgroundColor: isChildActive ? "var(--primary-light)" : undefined,
                        borderColor: isChildActive ? "var(--primary-border)" : undefined,
                        color: isChildActive ? COLORS.primary : COLORS.textSecondary,
                      }}
                      aria-expanded={isExpanded}
                      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${item.label}`}
                      title={shouldShowCollapsed ? item.label : undefined}
                    >
                      <div
                        className="flex items-center justify-center w-5 h-5"
                        style={{
                          color: isChildActive ? COLORS.primary : COLORS.textMuted,
                        }}
                      >
                        <Icon size={20} />
                      </div>
                      {!shouldShowCollapsed && (
                        <>
                          <span className="ml-3 flex-1 text-left">{item.label}</span>
                          <div className="ml-2 transition-transform" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                            <MdExpandMore size={18} />
                          </div>
                        </>
                      )}
                    </button>

                    {/* Children items - Expanded view */}
                    {isExpanded && !shouldShowCollapsed && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.children?.map((child) => {
                          const ChildIcon = child.icon;
                          return (
                            <button
                              key={child.id}
                              onClick={() => handlePageChange(child.id as PageId)}
                              className={`
                                flex items-center w-full p-2.5 pl-4 rounded-lg text-sm font-medium transition-colors focus:outline-none
                                ${currentPage === child.id ? 'border' : 'hover:bg-gray-50'}
                              `}
                              style={{
                                backgroundColor: currentPage === child.id ? "var(--primary-light)" : undefined,
                                borderColor: currentPage === child.id ? "var(--primary-border)" : undefined,
                                color: currentPage === child.id ? COLORS.primary : COLORS.textSecondary,
                              }}
                              aria-current={currentPage === child.id ? "page" : undefined}
                              aria-label={`Navigate to ${child.label}`}
                            >
                              <div
                                className="flex items-center justify-center w-4 h-4"
                                style={{
                                  color: currentPage === child.id ? COLORS.primary : COLORS.textMuted,
                                }}
                              >
                                <ChildIcon size={16} />
                              </div>
                              <span className="ml-2">{child.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Collapsed view - click popup menu (fixed position to avoid overflow clipping) */}
                    {shouldShowCollapsed && collapsedMenuOpen === item.id && (
                      <div
                        className="fixed left-20 z-[100]"
                        style={{
                          minWidth: '180px',
                          marginTop: '-44px'
                        }}
                      >
                        <div
                          className="bg-white rounded-lg shadow-lg border py-2"
                          style={{ borderColor: "var(--border-primary)" }}
                        >
                          <div
                            className="px-3 py-2 text-sm font-semibold border-b mb-1"
                            style={{ color: COLORS.textPrimary, borderColor: "var(--border-light)" }}
                          >
                            {item.label}
                          </div>
                          {item.children?.map((child) => {
                            const ChildIcon = child.icon;
                            return (
                              <button
                                key={child.id}
                                onClick={() => {
                                  handlePageChange(child.id as PageId);
                                  setCollapsedMenuOpen(null);
                                }}
                                className={`
                                  flex items-center w-full px-3 py-2 text-sm font-medium transition-colors focus:outline-none
                                  ${currentPage === child.id ? '' : 'hover:bg-gray-50'}
                                `}
                                style={{
                                  backgroundColor: currentPage === child.id ? "var(--primary-light)" : undefined,
                                  color: currentPage === child.id ? COLORS.primary : COLORS.textSecondary,
                                }}
                                aria-current={currentPage === child.id ? "page" : undefined}
                                aria-label={`Navigate to ${child.label}`}
                              >
                                <div
                                  className="flex items-center justify-center w-4 h-4"
                                  style={{
                                    color: currentPage === child.id ? COLORS.primary : COLORS.textMuted,
                                  }}
                                >
                                  <ChildIcon size={16} />
                                </div>
                                <span className="ml-2">{child.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              // Regular item without children
              return (
                <button
                  key={item.id}
                  onClick={() => handlePageChange(item.id as PageId)}
                  className={`
                  flex items-center w-full p-3 rounded-lg text-sm font-medium transition-colors focus:outline-none
                  ${currentPage === item.id ? 'border' : 'hover:bg-gray-50'}
                  ${shouldShowCollapsed ? 'justify-center' : ''}
                `}
                  style={{
                    backgroundColor:
                      currentPage === item.id
                        ? "var(--primary-light)"
                        : undefined,
                    borderColor:
                      currentPage === item.id
                        ? "var(--primary-border)"
                        : undefined,
                    color:
                      currentPage === item.id
                        ? COLORS.primary
                        : COLORS.textSecondary,
                  }}
                  aria-current={currentPage === item.id ? "page" : undefined}
                  aria-label={`Navigate to ${item.label}`}
                  title={shouldShowCollapsed ? item.label : undefined}
                >
                  <div
                    className="flex items-center justify-center w-5 h-5"
                    style={{
                      color: currentPage === item.id
                        ? COLORS.primary
                        : COLORS.textMuted,
                    }}
                  >
                    <Icon size={20} />
                  </div>
                  {!shouldShowCollapsed && (
                    <span className="ml-3">{item.label}</span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t" style={{ borderColor: "var(--border-primary)" }}>
            <div className="relative user-menu-container">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className={`flex items-center w-full hover:bg-gray-50 rounded-lg py-2 px-3 transition-colors ${shouldShowCollapsed ? 'justify-center' : ''}`}
                aria-label="User menu"
              >
                <div
                  className="h-8 w-8 flex items-center justify-center border rounded-full flex-shrink-0"
                  style={{
                    backgroundColor: "var(--bg-primary)",
                    borderColor: "var(--border-primary)",
                  }}
                >
                  <svg
                    className="w-4 h-4 text-gray-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>

                {!shouldShowCollapsed && (
                  <div className="ml-3 text-left flex-1 min-w-0">
                    <div
                      className="text-sm font-semibold truncate"
                      style={{ color: COLORS.textPrimary }}
                    >
                      {user?.email || 'User'}
                    </div>
                    <div
                      className="text-xs truncate"
                      style={{ color: COLORS.textSecondary }}
                    >
                      {user?.role || 'Admin'}
                    </div>
                  </div>
                )}

                {!shouldShowCollapsed && (
                  <svg
                    className="w-4 h-4 ml-2 text-gray-400 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                )}
              </button>

              {/* User Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <div
                      className="text-sm font-medium"
                      style={{ color: COLORS.textPrimary }}
                    >
                      {user?.email || 'user@example.com'}
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: COLORS.textSecondary }}
                    >
                      {user?.role || 'Admin'}
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default SideNavigation;
